import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { paths } from '../../api/schema';
import { api, unwrap, type Schemas } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';

export type Conversation = Schemas['PlatformSupportConversation'];
export type Thread = Schemas['PlatformSupportThread'];
export type Message = Thread['messages'][number];
export type InboxQuery = NonNullable<
  paths['/platform/support/conversations']['get']['parameters']['query']
>;

/** How often an open thread and the inbox look for new shop messages (only while on screen). */
const THREAD_POLL_MS = 3_000;
const INBOX_POLL_MS = 5_000;

export const chatKeys = {
  all: ['platform', 'support-chat'] as const,
  list: (q: InboxQuery) => ['platform', 'support-chat', 'list', q] as const,
  thread: (id: string) => ['platform', 'support-chat', 'thread', id] as const,
  unread: ['platform', 'support-chat', 'unread'] as const,
};

export function useInbox(query: InboxQuery) {
  return useQuery({
    queryKey: chatKeys.list(query),
    queryFn: () => unwrap(api.GET('/platform/support/conversations', { params: { query } })),
    refetchInterval: INBOX_POLL_MS,
    placeholderData: (prev) => prev,
  });
}

export function useThread(id: string | undefined) {
  return useQuery({
    queryKey: chatKeys.thread(id ?? ''),
    queryFn: () =>
      unwrap(
        api.GET('/platform/support/conversations/{id}', {
          params: { path: { id: id ?? '' }, query: {} },
        }),
      ),
    enabled: Boolean(id),
    refetchInterval: THREAD_POLL_MS,
  });
}

export function fetchOlder(id: string, before: string) {
  return unwrap(
    api.GET('/platform/support/conversations/{id}', {
      params: { path: { id }, query: { before } },
    }),
  );
}

/** Conversations waiting on the platform (the Support inbox badge). */
export function useSupportUnread() {
  const { can } = useAuth();
  return useQuery({
    queryKey: chatKeys.unread,
    queryFn: () => unwrap(api.GET('/platform/support/unread')),
    enabled: can('support.chat'),
    refetchInterval: 30_000,
  });
}

function useInvalidate() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: chatKeys.all });
}

export function useMarkRead(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () =>
      unwrap(api.POST('/platform/support/conversations/{id}/read', { params: { path: { id } } })),
    onSuccess: invalidate,
  });
}

export function useReply(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    // A client id per message: a retried send is not duplicated.
    mutationFn: (input: { id: string; body: string }) =>
      unwrap(
        api.POST('/platform/support/conversations/{id}/messages', {
          params: { path: { id } },
          body: input,
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useSetStatus(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (status: Conversation['status']) =>
      unwrap(
        api.POST('/platform/support/conversations/{id}/status', {
          params: { path: { id } },
          body: { status },
        }),
      ),
    onSuccess: invalidate,
  });
}
